/**
 * Picks the one org unit an invited person joins, and whether they become its head.
 */
import { useMemo, useState } from 'react';
import { Checkbox, FormModal } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { OrgUnitTree } from '@/features/admin/components/code-lists/org-structure/OrgUnitTree';
import {
  collectExpandableIds,
  type OrgTreeNode,
  type OrgTreeUnit
} from '@/features/admin/components/code-lists/org-structure/use-org-structure';

export interface OrgUnitPick {
  unitId: string;
  asManager: boolean;
}

interface OrgUnitPickerModalProps {
  /** Units on offer, already limited to the active ones. */
  tree: OrgTreeNode<OrgTreeUnit>[];
  /** The current pick, preselected when the picker opens. */
  value: OrgUnitPick | null;
  onConfirm: (pick: OrgUnitPick) => void;
  onClose: () => void;
}

function findUnit(nodes: OrgTreeNode<OrgTreeUnit>[], id: string): OrgTreeUnit | null {
  for (const node of nodes) {
    if (node.unit.id === id) return node.unit;
    const found = findUnit(node.children, id);
    if (found) return found;
  }
  return null;
}

export function OrgUnitPickerModal({ tree, value, onConfirm, onClose }: OrgUnitPickerModalProps) {
  const t = useFeatureTranslation('systemSettings.users');
  const [selectedId, setSelectedId] = useState<string | null>(value?.unitId ?? null);
  const [asManager, setAsManager] = useState(value?.asManager ?? false);
  const [expandedIds, setExpandedIds] = useState(() => collectExpandableIds(tree));

  const currentHead = useMemo(
    () => (selectedId ? (findUnit(tree, selectedId)?.manager?.displayName ?? null) : null),
    [tree, selectedId]
  );

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <FormModal
      open={true}
      title={t('orgUnit.pickerTitle')}
      submitLabel={t('orgUnit.confirm')}
      cancelLabel={t('actions.cancel')}
      submitDisabled={!selectedId}
      onSubmit={() => {
        if (selectedId) onConfirm({ unitId: selectedId, asManager });
      }}
      onClose={onClose}
      testId='org-unit-picker-modal'
    >
      <div className='max-h-96 overflow-y-auto'>
        <OrgUnitTree
          nodes={tree}
          selectedId={selectedId}
          expandedIds={expandedIds}
          searchActive={false}
          onSelect={setSelectedId}
          onToggle={toggleExpanded}
        />
      </div>
      <Checkbox
        id='org-unit-as-manager'
        label={t('orgUnit.asManager')}
        description={
          asManager && currentHead ? t('orgUnit.replacesHead', { name: currentHead }) : undefined
        }
        checked={asManager}
        onChange={setAsManager}
      />
    </FormModal>
  );
}
